import React, { useEffect, useRef, useState } from "react";
import { Copy, ChevronDown, ChevronUp, ImagePlus, Pencil, Plus, Trash2, X } from "lucide-react";
import { adminApi, apiImageUrl, fmt, formatApiError } from "../../lib/shopApi";

const EMPTY_PRODUCT = {
  name: "",
  description: "",
  category: "Crown Collection",
  price_rands: "",
  sale_price_rands: "",
  sizes: [],
  stock: 0,
  lengths_text: "",
  colors_text: "",
  thicknesses_text: "",
  images: [],
  status: "active",
  featured: false,
  seo_title: "",
  seo_description: "",
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest text-[#F5EFE3]/50 mb-2">{label}</label>
    {children}
  </div>
);

const inputCls = "w-full bg-[#1C1C1C] border border-[#C9A24B]/20 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#C9A24B] transition-colors";

const STANDARD_CATEGORIES = ["Crown Collection", "Hair Products", "Locs Extensions"];

export const isExtensionsCategory = (category) => (category || "").trim().toLowerCase() === "locs extensions";

const ProductEditor = ({ product, existingCategories = [], onClose, onSaved }) => {
  const [form, setForm] = useState(() =>
    product
      ? {
          ...EMPTY_PRODUCT,
          ...product,
          price_rands: (product.price_cents / 100).toString(),
          sale_price_rands: product.sale_price_cents ? (product.sale_price_cents / 100).toString() : "",
          sizes: (product.sizes || []).map((s) => ({ ...s, price_rands: (s.price_cents / 100).toString() })),
          lengths_text: (product.options?.lengths || []).join(", "),
          colors_text: (product.options?.colors || []).join(", "),
          thicknesses_text: (product.options?.thicknesses || []).join(", "),
        }
      : EMPTY_PRODUCT
  );
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const r = await adminApi.post("/upload", data, { headers: { "Content-Type": "multipart/form-data" } });
      set("images", [...form.images, r.data.url]);
    } catch (e) {
      setError(formatApiError(e, "Image upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setError("");
    if (!form.name.trim()) {
      setError("Product name is required.");
      return;
    }
    setBusy(true);
    const isExtensions = isExtensionsCategory(form.category);
    const splitList = (text) => text.split(",").map((s) => s.trim()).filter(Boolean);
    const payload = {
      name: form.name,
      description: form.description,
      category: form.category,
      loc_stage: "all",
      price_cents: Math.round(parseFloat(form.price_rands || "0") * 100),
      sale_price_cents: form.sale_price_rands ? Math.round(parseFloat(form.sale_price_rands) * 100) : null,
      sizes: form.sizes.map((s) => ({ label: s.label, price_cents: Math.round(parseFloat(s.price_rands || "0") * 100), stock: parseInt(s.stock) || 0 })),
      stock: parseInt(form.stock) || 0,
      options: isExtensions
        ? { lengths: splitList(form.lengths_text), colors: splitList(form.colors_text), thicknesses: splitList(form.thicknesses_text) }
        : { lengths: [], colors: [], thicknesses: [] },
      made_to_order: isExtensions,
      lead_time_text: isExtensions ? "7-15 days" : null,
      images: form.images,
      status: form.status,
      featured: form.featured,
      seo_title: form.seo_title,
      seo_description: form.seo_description,
    };
    try {
      if (product) {
        await adminApi.put(`/products/${product.id}`, payload);
      } else {
        await adminApi.post("/products", payload);
      }
      onSaved();
    } catch (e) {
      setError(formatApiError(e, "Could not save product"));
    } finally {
      setBusy(false);
    }
  };

  const setSize = (i, key, value) => {
    const sizes = [...form.sizes];
    sizes[i] = { ...sizes[i], [key]: value };
    set("sizes", sizes);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm overflow-y-auto" data-testid="product-editor">
      <div className="max-w-2xl mx-auto my-6 bg-[#1C1C1C] border border-[#C9A24B]/20 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl">{product ? "Edit Product" : "New Product"}</h2>
          <button onClick={onClose} data-testid="editor-close-btn" className="p-2 text-[#F5EFE3]/50 hover:text-[#C9A24B]"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-5">
          <Field label="Name *">
            <input value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="editor-name" />
          </Field>
          <Field label="Description">
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className={inputCls} data-testid="editor-description" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Category">
              {(() => {
                const allCats = [...new Set([...STANDARD_CATEGORIES, ...existingCategories])];
                const isCustom = !allCats.includes(form.category);
                return (
                  <>
                    <select
                      value={isCustom ? "__custom" : form.category}
                      onChange={(e) => set("category", e.target.value === "__custom" ? "" : e.target.value)}
                      className={inputCls}
                      data-testid="editor-category"
                    >
                      {allCats.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                      <option value="__custom">+ Custom category…</option>
                    </select>
                    {isCustom && (
                      <input
                        value={form.category}
                        onChange={(e) => set("category", e.target.value)}
                        placeholder="Type your new category name"
                        className={`${inputCls} mt-2`}
                        data-testid="editor-category-custom"
                      />
                    )}
                  </>
                );
              })()}
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls} data-testid="editor-status">
                <option value="active">Active</option>
                <option value="hidden">Hidden</option>
                <option value="sold_out">Sold Out</option>
              </select>
            </Field>
          </div>

          {isExtensionsCategory(form.category) && (
            <div className="border border-[#C9A24B]/25 bg-[#C9A24B]/5 rounded-2xl p-5 space-y-4" data-testid="extensions-options">
              <p className="text-[#C9A24B] text-xs uppercase tracking-widest">
                Locs Extensions — made to order, ready in 7-15 days
              </p>
              <Field label="Lengths (comma separated, e.g. 10 inch, 12 inch, 14 inch)">
                <input value={form.lengths_text} onChange={(e) => set("lengths_text", e.target.value)} className={inputCls} data-testid="editor-lengths" />
              </Field>
              <Field label="Colors (comma separated, e.g. Natural Black, Brown, Blonde)">
                <input value={form.colors_text} onChange={(e) => set("colors_text", e.target.value)} className={inputCls} data-testid="editor-colors" />
              </Field>
              <Field label="Thickness (comma separated, e.g. Thin, Medium, Thick)">
                <input value={form.thicknesses_text} onChange={(e) => set("thicknesses_text", e.target.value)} className={inputCls} data-testid="editor-thicknesses" />
              </Field>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Price (R)">
              <input type="number" min="0" step="0.01" value={form.price_rands} onChange={(e) => set("price_rands", e.target.value)} className={inputCls} data-testid="editor-price" />
            </Field>
            <Field label="Sale Price (R) — optional">
              <input type="number" min="0" step="0.01" value={form.sale_price_rands} onChange={(e) => set("sale_price_rands", e.target.value)} className={inputCls} data-testid="editor-sale-price" />
            </Field>
          </div>

          {/* Sizes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-widest text-[#F5EFE3]/50">Size Variants (optional)</label>
              <button
                onClick={() => set("sizes", [...form.sizes, { label: "", price_rands: "", stock: 0 }])}
                data-testid="add-size-btn"
                className="text-[#C9A24B] text-xs uppercase tracking-widest flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" /> Add Size
              </button>
            </div>
            {form.sizes.map((s, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center">
                <input placeholder="e.g. 100ml" value={s.label} onChange={(e) => setSize(i, "label", e.target.value)} className={`${inputCls} col-span-4`} data-testid={`size-label-${i}`} />
                <input type="number" min="0" step="0.01" placeholder="Price R" value={s.price_rands} onChange={(e) => setSize(i, "price_rands", e.target.value)} className={`${inputCls} col-span-4`} data-testid={`size-price-${i}`} />
                <input type="number" min="0" placeholder="Stock" value={s.stock} onChange={(e) => setSize(i, "stock", e.target.value)} className={`${inputCls} col-span-3`} data-testid={`size-stock-${i}`} />
                <button onClick={() => set("sizes", form.sizes.filter((_, j) => j !== i))} className="col-span-1 text-[#F5EFE3]/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {form.sizes.length === 0 && (
              <Field label="Stock Quantity">
                <input type="number" min="0" value={form.stock} onChange={(e) => set("stock", e.target.value)} className={inputCls} data-testid="editor-stock" />
              </Field>
            )}
          </div>

          {/* Images */}
          <div>
            <label className="block text-xs uppercase tracking-widest text-[#F5EFE3]/50 mb-2">Images</label>
            <div className="flex flex-wrap gap-3 mb-3">
              {form.images.map((img, i) => (
                <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border border-[#C9A24B]/20">
                  <img src={apiImageUrl(img)} alt={`Product ${i + 1}`} className="w-full h-full object-cover" />
                  <button
                    onClick={() => set("images", form.images.filter((_, j) => j !== i))}
                    data-testid={`remove-image-${i}`}
                    className="absolute top-1 right-1 bg-black/70 rounded-full p-1 text-white/80 hover:text-red-400"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="upload-image-btn"
                className="w-20 h-20 rounded-xl border border-dashed border-[#C9A24B]/40 flex flex-col items-center justify-center text-[#C9A24B]/70 hover:border-[#C9A24B] transition-colors"
              >
                <ImagePlus className="w-5 h-5" />
                <span className="text-[9px] mt-1">{uploading ? "…" : "Upload"}</span>
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])}
              />
            </div>
          </div>
          <div className="flex items-center">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => set("featured", e.target.checked)}
                data-testid="editor-featured"
                className="w-5 h-5 accent-[#C9A24B]"
              />
              <span className="text-sm text-[#F5EFE3]/70">Featured on homepage</span>
            </label>
          </div>

          <Field label="SEO Title">
            <input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} className={inputCls} data-testid="editor-seo-title" />
          </Field>
          <Field label="SEO Description">
            <textarea value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} rows={2} className={inputCls} data-testid="editor-seo-description" />
          </Field>

          {error && <p className="text-red-400 text-sm" data-testid="editor-error">{error}</p>}

          <button
            onClick={save}
            disabled={busy}
            data-testid="editor-save-btn"
            className="w-full bg-[#C9A24B] text-[#1C1C1C] py-4 rounded-full text-xs font-bold uppercase tracking-widest hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save Product"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const ProductsTab = () => {
  const [products, setProducts] = useState(null);
  const [editing, setEditing] = useState(null); // null=closed, {}=new, product=edit
  const [categoryFilter, setCategoryFilter] = useState("all");

  const load = () => adminApi.get("/products").then((r) => setProducts(r.data.products)).catch(() => setProducts([]));
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    await adminApi.delete(`/products/${p.id}`);
    load();
  };

  const duplicate = async (p) => {
    await adminApi.post(`/products/${p.id}/duplicate`);
    load();
  };

  const move = async (p, dir) => {
    const list = [...products];
    const i = list.findIndex((x) => x.id === p.id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    setProducts(list);
    try {
      await adminApi.put("/products/reorder", { ids: list.map((x) => x.id) });
    } catch {
      load();
    }
  };

  if (!products) return <p className="text-[#F5EFE3]/40 text-sm py-10 text-center">Loading products…</p>;

  const categories = [...new Set(products.map((p) => p.category).filter(Boolean))];
  const visible = categoryFilter === "all" ? products : products.filter((p) => p.category === categoryFilter);

  return (
    <div data-testid="products-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-[#F5EFE3]/50 text-sm">
          {products.length} product{products.length === 1 ? "" : "s"}
          {categoryFilter === "all" && products.length > 1 && <span className="text-[#F5EFE3]/35"> — use the arrows to set shop order</span>}
        </p>
        <button
          onClick={() => setEditing({})}
          data-testid="new-product-btn"
          className="flex items-center gap-2 bg-[#C9A24B] text-[#1C1C1C] px-5 py-3 rounded-full text-xs font-bold uppercase tracking-widest hover:-translate-y-0.5 transition-transform duration-300"
        >
          <Plus className="w-4 h-4" /> New Product
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-3 mb-4">
        {["all", ...categories].map((c) => (
          <button
            key={c}
            onClick={() => setCategoryFilter(c)}
            data-testid={`product-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest whitespace-nowrap border transition-colors ${
              categoryFilter === c ? "bg-[#C9A24B] text-[#1C1C1C] border-[#C9A24B] font-bold" : "border-[#C9A24B]/20 text-[#F5EFE3]/55"
            }`}
          >
            {c === "all" ? "All" : c}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {visible.length === 0 && (
          <p className="text-[#F5EFE3]/40 text-sm py-10 text-center">No products in this category yet.</p>
        )}
        {visible.map((p) => (
          <div key={p.id} className="bg-[#232323] border border-[#C9A24B]/10 rounded-2xl p-4 flex flex-wrap items-center gap-4" data-testid={`admin-product-${p.slug}`}>
            <div className="w-14 h-14 rounded-xl overflow-hidden bg-[#181818] shrink-0">
              {p.images?.[0] && <img src={apiImageUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-[180px]">
              <p className="font-semibold leading-snug">{p.name}</p>
              <p className="text-[#F5EFE3]/45 text-xs mt-0.5">
                {fmt(p.sale_price_cents || p.price_cents)} · stock {p.total_stock}
                {p.featured && <span className="text-[#C9A24B]"> · featured</span>}
              </p>
            </div>
            <span className={`text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${
              p.status === "active" ? "border-green-400/40 text-green-300" : p.status === "sold_out" ? "border-amber-400/40 text-amber-300" : "border-white/20 text-white/50"
            }`}>
              {p.status.replace("_", " ")}
            </span>
            <div className="flex gap-1.5 items-center">
              {categoryFilter === "all" && (
                <div className="flex flex-col mr-1">
                  <button
                    onClick={() => move(p, -1)}
                    disabled={products.findIndex((x) => x.id === p.id) === 0}
                    data-testid={`move-up-${p.slug}`}
                    aria-label="Move product up"
                    className="p-1 text-[#F5EFE3]/50 hover:text-[#C9A24B] transition-colors disabled:opacity-20"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => move(p, 1)}
                    disabled={products.findIndex((x) => x.id === p.id) === products.length - 1}
                    data-testid={`move-down-${p.slug}`}
                    aria-label="Move product down"
                    className="p-1 text-[#F5EFE3]/50 hover:text-[#C9A24B] transition-colors disabled:opacity-20"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              )}
              <button onClick={() => setEditing(p)} data-testid={`edit-product-${p.slug}`} className="p-2.5 text-[#F5EFE3]/50 hover:text-[#C9A24B] transition-colors"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => duplicate(p)} data-testid={`duplicate-product-${p.slug}`} className="p-2.5 text-[#F5EFE3]/50 hover:text-[#C9A24B] transition-colors"><Copy className="w-4 h-4" /></button>
              <button onClick={() => remove(p)} data-testid={`delete-product-${p.slug}`} className="p-2.5 text-[#F5EFE3]/50 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {editing !== null && (
        <ProductEditor
          product={editing.id ? editing : null}
          existingCategories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};
